// src/lib/transferCardJobs.js
//
// #665, gate 665 D6-D13 and D21's answer line, Session 79.22b.4.5 -- THE WORDS
// ON THE TWO JOB CARDS, To make and To load, in one place.
// Gate: docs/gates/Stede_TransferJobCards_Gate_665.html, sections 3 and 7.
//
// ── What the two cards do, in plain words ──
//
// Owen, a fabricator at Houston, used to open a card, read a sheet, choose an
// item and type a number, one item at a time. Now the To make card lists each
// item with a box holding what is still to make, and ONE button signs them all
// off, together or not at all. The To load card does the same for a truck, and
// it is already set to go to Austin, because nearly every Houston load does.
//
// THE BUTTON'S OWN WORDS ARE THE CONFIRMATION -- the action, the number and the
// place (D22) -- so there is no "are you sure?". That makes these sentences load
// bearing rather than decoration: every one is the gate's copy table (F2), word
// for word, and transferCardJobs665.test.js holds each one to it.
//
// PURE: no database and no React, so every sentence is testable without a
// screen. The cards in TransfersPage.js draw them; they never compose their own.

import { isOwnShelf, madeList, andList, promisedDateFor } from './branchTransfers';
import { lineProgress, lineProgressText, transferProgress, makeWords } from './transferLoads';

const named = (branchNames, id, fallback) => branchNames?.[String(id)] || fallback;
const nameOfItem = (line, items) =>
  items?.[String(line?.item_id)]?.name || line?.inventory_items?.name || `Item ${line?.item_id}`;
const byLineId = (a, b) => Number(a.id) - Number(b.id);

// ── To make (D6, D7, Screen 1 and 1b) ─────────────────────────────────────

// The heading is the JOB, never the transfer number (callout A); the number
// and direction ride above it in small capitals so they never appear alone.
export function makeCardHeading(transfer, branchNames = {}) {
  return isOwnShelf(transfer)
    ? `Make for ${named(branchNames, transfer?.from_branch_id, 'this branch')}'s shelf`
    : `Make for ${named(branchNames, transfer?.to_branch_id, 'the other branch')}`;
}

// ── #800, Session 79.22b.6.5.2 -- EACH ITEM'S OWN PROMISE ON THE CARD ───────
// Approved drawing: docs/audits/79.22b.6.5.2/APPROVED_DRAWING_800.html.
//
// Houston can promise an item its own date (gate 517 D67). The card printed the
// request's date alone, so an item promised later read as due on the earlier
// date, and Welding counted it late while To make showed no date for it. The
// card's top line now names the range when the items' dates differ (callout A),
// and an item whose own date differs from the request's says so on its own line
// (B); a date after the needed date reads "later than needed" (C) -- the words
// and colour Dallas's card uses for the same thing (#618). A request with one
// date reads exactly as before. A refused item is not coming and lends no date.

const comingLines = transfer => (transfer?.branch_transfer_items || []).filter(l => l.line_status !== 'refused');

// { text, late } for the card's "Promised" line, or null when nothing is
// promised. `late` (the amber) only when the dates differ and some come after
// the needed date.
export function promisedLine(transfer) {
  const lines = comingLines(transfer);
  const dates = lines.map(l => promisedDateFor(l, transfer)).filter(Boolean).sort();
  if (!dates.length) {
    return transfer?.confirmed_date && !(transfer?.branch_transfer_items || []).length
      ? { text: `Promised ${transfer.confirmed_date}`, late: false } : null;
  }
  const first = dates[0];
  const last = dates[dates.length - 1];
  if (first === last) return { text: `Promised ${first}`, late: false };
  const needed = transfer?.needed_by;
  const lateItems = needed ? lines.filter(l => { const p = promisedDateFor(l, transfer); return !!p && p > needed; }).length : 0;
  return {
    text: `Promised ${first} to ${last}${lateItems ? ` · ${lateItems} ${lateItems === 1 ? 'item' : 'items'} later than needed` : ''}`,
    late: lateItems > 0,
  };
}

// The words an item's line adds when its own date differs from the request's,
// or null. `late` when that date is after the needed date.
export function itemPromise(line, transfer) {
  const own = line?.promised_date;
  if (!own || line?.line_status === 'refused' || own === transfer?.confirmed_date) return null;
  const late = !!transfer?.needed_by && own > transfer.needed_by;
  return { text: `promised ${own}${late ? ', later than needed' : ''}`, late };
}

// An item line's small grey words with its own promise after them.
function withPromise(sub, line, transfer) {
  const p = itemPromise(line, transfer);
  return p ? { sub: `${sub} · ${p.text}`, subLate: p.late } : { sub, subLate: false };
}

// A box per item holding what is STILL TO MAKE -- untouched means all of it.
// An item with nothing left to make, or refused, is named with its "x of y"
// and gets no box (gate 665 F1, Partial).
export function makeCardLines(transfer, items = {}) {
  return [...(transfer?.branch_transfer_items || [])].sort(byLineId).map(line => {
    const refused = line.line_status === 'refused';
    const p = lineProgress(line);
    return {
      item_id: line.item_id,
      line,
      name: nameOfItem(line, items),
      max: refused ? 0 : p.toMake,
      noBox: refused || p.toMake <= 0,
      refused,
      ...withPromise(lineProgressText('to_make', line), line, transfer),
    };
  });
}

export function signOffButtonLabel(total, transfer, branchNames = {}) {
  if (!(total > 0)) return 'Sign off';
  const maker = named(branchNames, transfer?.from_branch_id, 'the branch');
  return isOwnShelf(transfer)
    ? `Sign off ${total} onto ${maker}'s shelf`
    : `Sign off ${total} onto ${maker}'s rack`;
}

// #704, Session 79.22b.4.6.1c -- THE CARD'S SECOND DOOR HAS A NAME NOW. Every
// footer below told people to "open the transfer" while nothing on the card was
// called that; the card's top was the only way in, and nothing said it opened.
// Each card that acts now carries an "Open transfer" button, and all three
// footers name it in the same words (Colby's R1: "all three point at the
// button"), built from this one label so they cannot drift apart.
export const OPEN_TRANSFER_LABEL = 'Open transfer';
export const SIGN_OFF_DOOR = `Made a different number? Change its box. Anything else? ${OPEN_TRANSFER_LABEL}.`;
export function signOffFooter(transfer, branchNames = {}) {
  return isOwnShelf(transfer)
    ? `Nothing is loaded — ${named(branchNames, transfer?.from_branch_id, 'this branch')} asked for its own shelf.`
    : SIGN_OFF_DOOR;
}

// The refusals, each the sentence the screen already uses somewhere.
export const SAY_HOW_MANY_MADE = 'Say how many you made.';
export const NEEDS_THE_SWITCH = 'Signing off production needs a branch manager, or the switch on your account.';
// #705 -- the verb follows the number: "Only 1 Stake H-Wire is left to make".
export function overMakeCeiling(line) {
  return `Only ${line.max} ${line.name} ${Number(line.max) === 1 ? 'is' : 'are'} left to make. Sign off ${line.max} or fewer.`;
}

// ── The answer, on the card (D21's line; its Undo arrives with #694) ────────

// "Signed off 50 Stake H-Wire · 50 of 100 made" -- from what the DATABASE
// returned, never from what was typed. `signed` is signOffProductionLines()'s
// own `lines`.
//
// #713, 682 D11, Session 79.22b.6c.3 -- WITH GOODS FROM THE SHELF ON THE LINE,
// THE ANSWER SPLITS READY INTO MADE AND SHELF, as the card's own line does:
// "Signed off 10 Stake Push 24 · 30 of 60 ready · 10 made, 20 from the shelf".
// Made after the press is the line's runs before it (`transfer`'s lines carry
// `made_by_runs`, withMadeCounts()) plus this press. Without shelf goods, or
// with the runs unread, the approved wording stands. Two items with shelf goods
// on either say each item's own clause, in the same order as the names.
export function signOffDoneLine(signed, transfer = null) {
  const rows = signed || [];
  if (rows.length >= MANY_SIGNED) return `Signed off ${signedTotal(rows)}`;
  const byLine = new Map((transfer?.branch_transfer_items || []).map(l => [String(l.id), l]));
  const madeAfter = (s) => {
    const before = byLine.get(String(s.lineId))?.made_by_runs;
    return before == null ? null : Number(before) + (Number(s.qty) || 0);
  };
  const split = rows.some(s => { const m = madeAfter(s); return m != null && Number(s.readyNow) > m; });
  if (!split) return `Signed off ${madeList(rows)} · ${andList(rows.map(s => `${s.readyNow} of ${s.requested}`))} made`;
  return `Signed off ${madeList(rows)} · ${rows.map(s => makeWords({ ready: s.readyNow, requested: s.requested, made: madeAfter(s) })).join('; ')}`;
}

// #724, Session 79.22b.4.2.1 -- THREE OR MORE ITEMS IN ONE PRESS ANSWER WITH A
// TOTAL. One sentence naming six items and then six "2 of 2"s read as seven
// bold lines on a phone that nobody could pair up (Colby: "this looks bad and
// is hard to read"). The card's own rows, directly above the answer, already
// show each item's "x of y made", so the answer does not repeat them. One and
// two items keep gate 665 D7's approved wording. Colby's choice, 79.22b.4.2.1.
const MANY_SIGNED = 3;
function signedTotal(rows) {
  const total = rows.reduce((s, r) => s + (Number(r?.qty) || 0), 0);
  return `${total} across ${rows.length} items`;
}

// ── Undo (D21, #694, Session 79.22b.4.6) ──────────────────────────────────
//
// Offered for exactly the runs the press wrote, and ONLY when the database named
// every one: an answer without them came from a body older than
// 20260927_undo_sign_off_694, and a button that cannot be honoured is a promise
// the system cannot keep. It stays for as long as the answer does - until the
// next press or a change of tab - because the database, not a clock, decides
// whether it can still be taken back (Colby's ruling at 79.22b.4.6's open).
export function signOffUndo(signed) {
  const runs = signed || [];
  return runs.length > 0 && runs.every(s => s && s.productionId != null)
    ? { job: 'sign_off', signed: runs }
    : null;
}
export const UNDO_LABEL = 'Undo';
// The answer after an Undo. Not the gate's "Nothing from that press is left":
// the message the press sent stays in people's bells (R2), so that sentence
// would not be true. The counts are what the Undo put back.
export const UNDONE_LINE = 'Undone — the counts are back as they were.';

// What the press leaves behind, Screen 1b: "150 still to make. To load now
// shows 50 ready." The signed items' numbers are the database's; the rest are
// the transfer's own, untouched by the press.
export function signOffAfterNote(transfer, signed, branchNames = {}, { finished = false } = {}) {
  const readyAfter = new Map((signed || []).map(s => [String(s.lineId), s.readyNow]));
  const lines = (transfer?.branch_transfer_items || []).map(l => (
    readyAfter.has(String(l.id)) ? { ...l, qty_ready: readyAfter.get(String(l.id)) } : l));
  const p = transferProgress(lines);
  if (isOwnShelf(transfer)) {
    const shelf = `${named(branchNames, transfer?.from_branch_id, 'this branch')}'s shelf`;
    // #705 -- "All 1 are on Houston's shelf" read wrong for a request of one.
    return finished || p.toMake === 0
      ? (p.requested === 1
        ? `The 1 is on ${shelf}. This request is finished.`
        : `All ${p.requested} are on ${shelf}. This request is finished.`)
      : `${p.toMake} still to make for ${shelf}.`;
  }
  return p.toMake > 0
    ? `${p.toMake} still to make. To load now shows ${p.readyToLoad} ready.`
    : `Everything is made. To load now shows ${p.readyToLoad} ready.`;
}

// ── To load (D8, D9, D11, D12, D13, Screen 2) ─────────────────────────────

export function loadCardHeading(transfer, branchNames = {}) {
  return `Load for ${named(branchNames, transfer?.to_branch_id, 'the other branch')}`;
}

// A box per item holding what is made and READY -- the ceiling a truck can
// take (553 D4). An item with none ready, or refused, is named and not boxed.
export function loadCardLines(transfer, items = {}) {
  return [...(transfer?.branch_transfer_items || [])].sort(byLineId).map(line => {
    const refused = line.line_status === 'refused';
    const p = lineProgress(line);
    return {
      item_id: line.item_id,
      line,
      name: nameOfItem(line, items),
      max: refused ? 0 : p.readyToLoad,
      noBox: refused || p.readyToLoad <= 0,
      refused,
      ...withPromise(lineProgressText('to_load', line), line, transfer),
    };
  });
}

// The route, already answered (callout F). `hub` is defaultRoute()'s answer,
// the same one the load sheet uses. No hub, or the hub is an end: no choice.
export function routeWords({ transfer, hub, branchNames = {} }) {
  const dest = named(branchNames, transfer?.to_branch_id, 'the other branch');
  if (!hub) return { choice: false, destName: dest, text: `Driving straight to ${dest}` };
  const hubName = named(branchNames, hub.id, hub.name || 'the hub');
  return {
    choice: true,
    destName: dest,
    hubName,
    viaLabel: `Via ${hubName} · ${dest} collects there`,
    straightLabel: `Straight to ${dest}`,
  };
}

export function sendButtonLabel(total, route, via) {
  if (!(total > 0)) return 'Send';
  if (route?.choice && via) return `Send ${total} to ${route.hubName}`;
  if (route?.choice) return `Send ${total} straight to ${route.destName}`;
  return `Send ${total} to ${route?.destName || 'its destination'}`;
}

// Said BEFORE Send, so a wrong default is visible rather than silent (callout G).
export const DRIVER_NAMED_LATER = 'Driver: named when it is collected or counted in';
export function driverFact({ loaderMode = false, pick = '', currentUserId = null } = {}) {
  if (loaderMode) return DRIVER_NAMED_LATER;
  return pick && currentUserId != null && String(pick) === String(currentUserId)
    ? 'Driving: you'
    : 'Driving: No driver recorded';
}

// "Driving straight to Austin · Driving: you · This load: HOU-TRF-00044-1".
// A null reference is still being read, and says "…" rather than a guess (#671).
export function loadFactsLine({ route, driverText, ref }) {
  return [route && !route.choice ? route.text : null, driverText, `This load: ${ref || '…'}`]
    .filter(Boolean).join(' · ');
}

export const LOAD_CARD_DOOR = `A handover on the road, another driver or a truck number? ${OPEN_TRANSFER_LABEL}.`;
// The To collect card's, which lived inline in TransfersPage.js until #704.
//
// #740, Session 79.22b.6e -- THE FOOTER PROMISES ONLY WHAT THE SHEET CAN DO.
// It said "count it and record the shortfall" on every card, and the sheet
// offers a count only where exactly ONE truck is being met at the stop (gate
// 553 D11, `canCount` in the collect panel: a count corrects what is on that
// truck, never what was already standing). For three loads in four - goods
// already on the ground, or several trucks at one stop - Dallas's driver could
// only take less. Colby chose fix A (R1): the promise only where it is kept,
// and elsewhere what to do. The place is named (#729), and the rule is the
// panel's own, read from the same stop object (`loads` = trucks being met).
export function collectCardDoor(stop, branchNames = {}) {
  const where = stop?.place || named(branchNames, stop?.branchId, null);
  const less = where ? `Less at ${where} than this?` : 'Less than this?';
  return (stop?.loads || []).length === 1
    ? `${less} ${OPEN_TRANSFER_LABEL} to count it and record the shortfall.`
    : `${less} Take what is there and tell a manager.`;
}
// The load sheet's own sentence (517 D51), said only when something stays behind.
export function loadCardFooter(transfer, total) {
  const p = transferProgress(transfer?.branch_transfer_items || []);
  const owed = Math.max(p.requested - p.shipped - (Number(total) || 0), 0);
  // #705 -- "After this, 1 are still owed" read wrong.
  return owed > 0
    ? `After this, ${owed} ${owed === 1 ? 'is' : 'are'} still owed and the request stays open. ${LOAD_CARD_DOOR}`
    : LOAD_CARD_DOOR;
}

export const CHECKING_TRUCKS = 'Checking what is already on trucks…';
export const TYPE_HOW_MANY_LOAD = 'Type how many are going on this truck.';
// #705 -- "Only 1 of Frame WA 24x18 is standing here", as the load sheet says it.
export function overLoadCeiling(line) {
  return `Only ${line.max} of ${line.name} ${Number(line.max) === 1 ? 'is' : 'are'} standing here. Send that or less.`;
}

// "Sent 25 to Austin as HOU-TRF-00008-3" -- the reference the database issued.
export function sendDoneLine({ total, place, ref }) {
  return ref ? `Sent ${total} to ${place} as ${ref}` : `Sent ${total} to ${place}`;
}

// #694, Session 79.22b.4.6.1 -- THE SEND'S UNDO, offered only when the database
// named the truck: undo_load() finds it by the reference load_branch_transfer()
// issued, so an answer without one gets no button - a button that cannot be
// honoured is a promise the system cannot keep. It lasts as long as the answer
// does, as the sign-off's does (Colby's R2 at 79.22b.4.6).
// #697 -- `collected`: the same truck, pressed as a pickup at a hub by the
// branch it is for, so its Undo answer says "collection" rather than "send".
export function sendUndo({ transferId, loadRef, collected = false } = {}) {
  const ref = typeof loadRef === 'string' ? loadRef.trim() : '';
  if (transferId == null || !ref) return null;
  return collected ? { job: 'send', transferId, loadRef: ref, collected: true } : { job: 'send', transferId, loadRef: ref };
}

// ── A hub's own goods, collected at the hub (#697, gate 665 D23, Session
// 79.22b.5.2; the words approved by Colby at its open, R1) ─────────────────────
//
// The hub's own card: what it holds for the other branch, and that nothing is
// for the hub to press. "Dallas collects this here" is D23's own sentence.
// #729, Session 79.22b.6e -- D23 AMENDED, approved as drawn (R2): the hub is
// named rather than "here", because these cards are read under All branches
// too. "Dallas collects this at Austin" / "Nothing for Austin to send."
export function hubCollectHeading(transfer, branchNames = {}) {
  const hub = named(branchNames, transfer?.from_branch_id, null);
  return `${named(branchNames, transfer?.to_branch_id, 'The other branch')} collects this ${hub ? `at ${hub}` : 'here'}`;
}
export function hubCollectNote(transfer, branchNames = {}) {
  const dest = named(branchNames, transfer?.to_branch_id, 'the other branch');
  const hub = named(branchNames, transfer?.from_branch_id, 'this branch');
  return `${dest}'s driver takes it from ${hub}'s shelf. Nothing for ${hub} to send.`;
}
// The receiving branch's card (Screen 10's right-hand card): "Austin's own
// shelf · 6 in total", and the pile card's own button words.
// A card kept in place after its press passes no total: it still says where the
// goods stood, never "Sent by", because nothing was sent (found on screen).
export function hubPickupSubline(transfer, branchNames = {}, total = 0) {
  const shelf = `${named(branchNames, transfer?.from_branch_id, 'The hub')}'s own shelf`;
  return total == null ? shelf : `${shelf} · ${total} in total`;
}
export function hubPickupLabel(total, leaving, hubName) {
  if (!(total > 0)) return 'Collect';
  return leaving > 0 ? `Take ${total}, leave ${leaving}` : `Collect ${total} from ${hubName || 'the hub'}`;
}

// ── To collect (D21's answer on the card, #694's Undo, 79.22b.4.6.1b) ──────
//
// "Collected 5 from Austin as HOU-TRF-00032-2" -- gate 665 F2's Done line with
// the number of the truck the driver is now on, as the Send's line carries its
// (Colby's R1 at 79.22b.4.6.1b). Take nothing keeps the sentence the page used.
export function collectDoneLine({ total, place, ref, nothing = false } = {}) {
  const at = place || 'the handover';
  if (nothing) return `Nothing went on your truck. Everything is still at ${at}.`;
  return ref ? `Collected ${total} from ${at} as ${ref}` : `Collected ${total} from ${at}`;
}

// WHAT STAYS BEHIND IS SAID OUT LOUD (#529): a driver who takes half needs to
// hear that the rest is still somebody's job, and where. Right for one and for
// many (#705) -- the sentence it replaces read "3 is still at Austin".
export function collectAfterNote({ leaving, place } = {}) {
  const n = Number(leaving) || 0;
  if (!(n > 0)) return null;
  const at = place || 'the handover';
  return n === 1
    ? `1 is still at ${at} — it stays on To collect until somebody fetches it.`
    : `${n} are still at ${at} — they stay on To collect until somebody fetches them.`;
}

// THE COLLECTION'S UNDO, offered only when the database named the record it
// wrote: undo_collection() finds the collection by nothing else, and a button
// that cannot be honoured is a promise the system cannot keep.
export function collectUndo({ handoverId } = {}) {
  const id = handoverId == null ? NaN : Number(handoverId);
  return Number.isFinite(id) ? { job: 'collect', handoverId: id } : null;
}

// ── To count in (D21's answer on the card, #694's Undo, 79.22b.4.6.2) ───────
//
// "Counted in 10 at Dallas from HOU-TRF-00047-2" -- gate 665 F2's Done line
// ("Counted in {n} at {branch}") with the truck it came off, as Send's and
// Collect's carry theirs. Approved by Colby at 79.22b.4.6.2's open (R1).
export function countInDoneLine({ total, branch, ref } = {}) {
  const at = branch || 'the receiving branch';
  return ref ? `Counted in ${total} at ${at} from ${ref}` : `Counted in ${total} at ${at}`;
}

// UNDER IT, what the count left behind: a gap, named against the truck it is
// recorded on, and whether it finished the request -- the sentences the answer
// at the top of the page carried (R1's drawing).
//
// #682, Session 79.22b.6c.2b -- A COUNT ABOVE THE TRUCK IS A QUESTION NOW, never
// "more than was sent": the truck's number was booked and the sending branch
// asked about the rest (682 D7), so the line under it is P5's second sentence,
// "Houston has been asked to confirm the other 2." `asked` is the database's own
// list of the questions it opened; `sender` names the branch asked.
export function countInAfterNote({ missing = 0, extra = 0, ref = null, complete = false, asked = [], sender = null } = {}) {
  const short = Number(missing) || 0;
  const over = Number(extra) || 0;
  const askedExtra = (Array.isArray(asked) ? asked : []).reduce((n, a) => n + (Number(a?.extra) || 0), 0);
  const parts = [];
  if (short > 0) {
    parts.push(ref ? `${short} short of what was sent — recorded against ${ref}.` : `${short} short of what was sent.`);
  }
  if (askedExtra > 0) {
    parts.push(`${sender || 'The sending branch'} has been asked to confirm the other ${askedExtra}.`);
  } else if (over > 0 && !(short > 0)) {
    parts.push(`${over} more than was sent.`);
  }
  if (complete) parts.push('That completes this request.');
  return parts.length ? parts.join(' ') : null;
}

// THE COUNT'S UNDO, offered only when the database named the record it wrote:
// undo_count_in() finds the count by nothing else, and a button that cannot be
// honoured is a promise the system cannot keep.
export function countInUndo({ countInId } = {}) {
  const id = countInId == null ? NaN : Number(countInId);
  return Number.isFinite(id) ? { job: 'count_in', countInId: id } : null;
}

// UNDER THE ANSWER AFTER A COUNT'S UNDO: the average and the request, when the
// count had moved them. The branch's average is named without a figure -- the
// person counting may be a team member, who is not shown money (#530).
export function countInUndoneAfter({ costRestored = false, reopened = false, branch = null } = {}) {
  const cost = branch ? `${branch}'s average cost` : 'The average cost';
  if (costRestored && reopened) return `${cost} is back to what it was, and the request is open again.`;
  if (costRestored) return `${cost} is back to what it was.`;
  if (reopened) return 'The request is open again.';
  return null;
}

// ── After an Undo -- #694, amended at 79.22b.4.6.1's close; the words approved
// by Colby at 79.22b.4.6.1b's open (R1) ──────────────────────────────────────
//
// WHAT WAS UNDONE, WHEN, AND WHAT CAME BACK, the same shape for every press:
// "You undid the send of 5 at 12:14 PM — HOU-TRF-00032-1 is off the road and
// the 5 are back on Houston's shelf." "You", because only the person who
// pressed can undo and the answer shows only on their own screen -- the
// history names them, and the time, for everybody else. No reason is asked
// (R3 at 79.22b.4.6.1's close): a reason belongs to the later correction
// (#702, #682). The time is the phone's clock as the answer lands (R2 here);
// the history keeps the database's own. UNDONE_LINE above stays as the answer
// when the press or its result cannot be read, rather than a guess.
export function undoneTime(at) {
  const d = at instanceof Date ? at : new Date(at);
  if (Number.isNaN(d.getTime())) return null;
  // Newer browsers put a narrow no-break space before "PM"; one plain space
  // reads the same and compares the same everywhere.
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }).replace(/[  ]/g, ' ');
}
// Right for one and for many (#705).
const theyAreBack = (n) => (Number(n) === 1 ? 'the 1 is back' : `the ${n} are back`);

export function undoneLine({ undo, result = null, at = new Date(), shelf = null, place = null } = {}) {
  const when = undoneTime(at);
  const t = when ? ` at ${when}` : '';
  const onShelf = shelf || 'the shelf';
  switch (undo?.job) {
    case 'sign_off': {
      // #724 -- the Undo answer takes the same shape as the answer it replaces.
      const what = (undo.signed || []).length >= MANY_SIGNED ? signedTotal(undo.signed) : madeList(undo.signed);
      if (!what) return UNDONE_LINE;
      return `You undid the sign-off of ${what}${t} — ${onShelf} and the made count are back.`;
    }
    case 'send': {
      const n = (result?.lines || []).reduce((s, l) => s + (Number(l?.qty) || 0), 0);
      if (!(n > 0) || !undo.loadRef) return UNDONE_LINE;
      // #697 -- a pickup at a hub is the same truck, pressed as a collection.
      const what = undo.collected ? 'collection' : 'send';
      return `You undid the ${what} of ${n}${t} — ${undo.loadRef} is off the road and ${theyAreBack(n)} on ${onShelf}.`;
    }
    case 'collect': {
      const legRefs = (result?.legs || []).map(l => l?.loadRef).filter(Boolean);
      const legs = andList(legRefs);
      // TAKE NOTHING: the driver's truck never existed, so what comes back is
      // the truck it recorded as handed over -- on the road again, as it was.
      if (!result?.loadRef) {
        if (!legs) return UNDONE_LINE;
        return legRefs.length > 1
          ? `You undid the handover${t} — ${legs} are back on the road as they were.`
          : `You undid the handover${t} — ${legs} is back on the road as it was.`;
      }
      const n = Number(result.taken) || 0;
      // A HANDOVER ON THE ROAD never left the other truck once it is undone;
      // at a yard, the pile is waiting there again.
      const back = result.atPlace && legs
        ? `${theyAreBack(n)} with ${legs}`
        : `${theyAreBack(n)} waiting at ${place || result.atPlace || 'the handover'}`;
      return `You undid the collection of ${n}${t} — ${result.loadRef} is off the road and ${back}.`;
    }
    case 'count_in': {
      // #694, 79.22b.4.6.2 -- the stock comes back OFF the shelf the count put
      // it on, and the truck is waiting to be counted again (R1's words).
      const ref = result?.loadRef || null;
      if (!ref) return UNDONE_LINE;
      const n = Number(result?.counted) || 0;
      const waiting = `${ref} is waiting to be counted again`;
      if (!(n > 0)) return `You undid the count${t} — ${waiting}.`;
      const off = n === 1 ? 'the 1 is off' : `the ${n} are off`;
      return `You undid the count of ${n}${t} — ${waiting} and ${off} ${onShelf}.`;
    }
    default:
      return UNDONE_LINE;
  }
}
